import React, { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type { AuthSession } from '../flows/auth-session';
import type { PendingAction } from '../types/ports';
import type { AuthProviderFeatures, SocialProfile, SocialProvider } from '../types/results';
import type { User } from '../types/user';
import type {
  ServiceSignInResult,
  ServiceSignUpResult,
  ServiceForgotPasswordResult,
  ServiceValidationResult,
  SignUpProps,
  ValidateUserRequest,
} from '../types/results';

/**
 * Reactive auth state and methods exposed by `useAuth()`.
 *
 * State fields (`user`, `isLoaded`, `isSignedIn`) update reactively via
 * `useSyncExternalStore`. Method references are stable across renders.
 */
export interface AuthState {
  /** The authenticated user, or `null` when signed out / not yet loaded. */
  user: User | null;
  /** `true` once the initial session bootstrap has resolved. */
  isLoaded: boolean;
  /** Shorthand for `user !== null`. */
  isSignedIn: boolean;

  // ── Session lifecycle ───────────────────────────────────────────────
  supportedFeatures: AuthProviderFeatures;
  signOut: (redirectUrl?: string) => Promise<void>;
  setUser: (user: User) => void;
  refreshSession: () => Promise<void>;
  getSignInProvider: () => Promise<string | null>;
  getLogoutUrl: (redirectUri: string) => string | null;

  // ── Social auth ─────────────────────────────────────────────────────
  /** Starts social sign-in via full-page redirect. */
  signInWithSocial: (provider: SocialProvider) => void;
  /** @deprecated Legacy client-app path only. */
  legacySocialSignIn: (profile: SocialProfile) => Promise<void>;
  hasPendingAfterLogout: () => boolean;
  resumeAfterLogout: () => void;
  failSocialAuth: (error: string) => void;

  // ── Account settings ────────────────────────────────────────────────
  submitPhone: (phone: string, profile?: { firstName: string; lastName: string }) => Promise<{ ok: true } | { ok: false; error: string }>;
  deleteAccount: () => Promise<{ ok: true } | { ok: false; error: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  updateProfile: (params: User) => Promise<{ ok: true } | { ok: false; error: string }>;
  updateEmail: (currentUser: User, newEmail: string, retypeEmail: string, password?: string, passwordConfirmation?: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  confirmEmailUpdate: (code: string) => Promise<{ ok: true } | { ok: false; error: string }>;

  // ── Pending actions ─────────────────────────────────────────────────
  consumePendingAction: () => PendingAction | null;
  peekPendingAction: () => PendingAction | null;
  clearPendingAction: () => void;

  // ── Social profile (cross-redirect name from social token) ──────────
  getSocialProfile: () => { firstName: string; lastName: string } | null;

  // ── Email/password auth (UserContext bridge) ────────────────────────
  signIn: (email: string, password: string) => Promise<ServiceSignInResult>;
  signUp: (props: SignUpProps) => Promise<ServiceSignUpResult>;
  confirmSignUp: (email: string, code: string) => Promise<ServiceSignUpResult>;
  resendSignUpCode: (email: string) => Promise<void>;
  validateUser: (request: ValidateUserRequest) => Promise<ServiceValidationResult>;
  forgotPassword: (email: string, theme: string) => Promise<ServiceForgotPasswordResult>;
  confirmForgotPassword: (email: string, code: string, password: string, theme: string) => Promise<ServiceForgotPasswordResult>;
  completeAuth: (user: User) => void;
}

const AuthContext = createContext<AuthState | null>(null);

const InternalSessionContext = createContext<AuthSession | null>(null);

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (!state) {
    throw new Error('useAuth must be used within an <AuthProvider>. Wrap your app in <AuthProvider> in _app.tsx.');
  }
  return state;
}

/**
 * Package-internal hook — returns the full AuthSession class.
 * NOT exported from the barrel. Used by useSignIn, useSignUp, useAuthEvent
 * that need access to internals (service, eventBus, etc.).
 */
export function useAuthSessionInternal(): AuthSession {
  const session = useContext(InternalSessionContext);
  if (!session) {
    throw new Error('useAuthSessionInternal must be used within an <AuthProvider>.');
  }
  return session;
}

interface AuthProviderProps {
  session: AuthSession;
  children: ReactNode;
}

export function AuthProvider({ session, children }: AuthProviderProps) {
  const snapshot = useSyncExternalStore(
    session.subscribe,
    session.getSnapshot,
    session.getServerSnapshot,
  );

  // Methods are bound once — session is a stable ref created in _app.tsx.
  // This memo never recomputes, so every function reference is stable across renders.
  const methods = useMemo(() => ({
    supportedFeatures: session.supportedFeatures,
    signOut: (r?: string) => session.signOut(r),
    setUser: (u: User) => session.setUser(u),
    refreshSession: () => session.refreshSession(),
    getSignInProvider: () => session.getSignInProvider(),
    getLogoutUrl: (uri: string) => session.getLogoutUrl(uri),

    signInWithSocial: (p: SocialProvider) => session.signInWithSocial(p),
    legacySocialSignIn: (p: SocialProfile) => session.legacySocialSignIn(p),
    hasPendingAfterLogout: () => session.hasPendingAfterLogout(),
    resumeAfterLogout: () => session.resumeAfterLogout(),
    failSocialAuth: (e: string) => session.failSocialAuth(e),

    submitPhone: (phone: string, profile?: { firstName: string; lastName: string }) => session.submitPhone(phone, profile),
    deleteAccount: () => session.deleteAccount(),
    changePassword: (cur: string, next: string) => session.changePassword(cur, next),
    updateProfile: (params: User) => session.updateProfile(params),
    updateEmail: (cur: User, email: string, retype: string, pw?: string, pwc?: string) => session.updateEmail(cur, email, retype, pw, pwc),
    confirmEmailUpdate: (code: string) => session.confirmEmailUpdate(code),

    consumePendingAction: () => session.consumePendingAction(),
    peekPendingAction: () => session.peekPendingAction(),
    clearPendingAction: () => session.clearPendingAction(),
    getSocialProfile: () => session.getSocialProfile(),

    signIn: (email: string, pw: string) => session.signIn(email, pw),
    signUp: (props: SignUpProps) => session.signUp(props),
    confirmSignUp: (email: string, code: string) => session.confirmSignUp(email, code),
    resendSignUpCode: (email: string) => session.resendSignUpCode(email),
    validateUser: (req: ValidateUserRequest) => session.validateUser(req),
    forgotPassword: (email: string, theme: string) => session.forgotPassword(email, theme),
    confirmForgotPassword: (email: string, code: string, pw: string, theme: string) => session.confirmForgotPassword(email, code, pw, theme),
    completeAuth: (user: User) => session.completeAuth(user),
  }), [session]);

  // Only recomputes when the snapshot's values actually change.
  const authState = useMemo((): AuthState => ({
    user: snapshot.user,
    isLoaded: snapshot.isLoaded,
    isSignedIn: snapshot.isSignedIn,
    ...methods,
  }), [snapshot.user, snapshot.isLoaded, snapshot.isSignedIn, methods]);

  return (
    <InternalSessionContext.Provider value={session}>
      <AuthContext.Provider value={authState}>
        {children}
      </AuthContext.Provider>
    </InternalSessionContext.Provider>
  );
}
